import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { X, PackageCheck, Ban, Copy, HelpCircle } from "lucide-react";

/**
 * ClosePODialog — unified "close this PO" flow.
 *
 * Handles the 4 non-delivery ways a PO leaves the active pipeline:
 *   • Already received manually (goods came in, booked in outside this loop)
 *   • Supplier cancelled the order
 *   • Duplicate (accidentally raised twice)
 *   • Other (free-text reason required)
 *
 * NEVER touches stock, movements or costs — purely a status flip on the
 * PO. Historical accounting stays intact.
 *
 * Reason → status mapping mirrors the backend `/po/{pid}/close`:
 *   manually_received / other → "closed" (grey pill)
 *   supplier_cancelled / duplicate → "cancelled" (grey pill)
 */
const REASONS = [
  {
    key: "manually_received",
    label: "Already received manually",
    hint: "Goods arrived and were booked in via the manual Receive flow. No stock/cost changes needed.",
    Icon: PackageCheck,
    tone: "#107A3E",
  },
  {
    key: "supplier_cancelled",
    label: "Supplier cancelled the order",
    hint: "Supplier called/emailed to say the order won't be fulfilled. PO becomes Cancelled.",
    Icon: Ban,
    tone: "#B0272D",
  },
  {
    key: "duplicate",
    label: "Duplicate — raised twice",
    hint: "Same order already exists on another PO. Kills this one, keeps the other intact.",
    Icon: Copy,
    tone: "#7A5300",
  },
  {
    key: "other",
    label: "Other reason",
    hint: "Free-text explanation required — will show on the PO detail dialog.",
    Icon: HelpCircle,
    tone: "#4B2E9B",
  },
];

const ClosePODialog = ({ open, po, onClose, onClosed }) => {
  const [reason, setReason] = useState("manually_received");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setReason("manually_received");
      setNotes("");
    }
  }, [open, po?.id]);

  if (!open || !po) return null;

  const submit = async () => {
    if (reason === "other" && !notes.trim()) {
      toast.error("Please add a note explaining the reason");
      return;
    }
    setBusy(true);
    try {
      await api.post(`/po/${po.id}/close`, {
        reason,
        notes: notes.trim(),
      });
      const label = reason === "supplier_cancelled" || reason === "duplicate"
        ? "cancelled"
        : "closed";
      toast.success(`${po.po_number} ${label}`);
      onClosed?.();
      onClose?.();
    } catch (e) {
      toast.error(
        e?.response?.data?.detail || "Could not close PO",
      );
    } finally {
      setBusy(false);
    }
  };

  const chosen = REASONS.find((r) => r.key === reason);
  const willCancel = reason === "supplier_cancelled" || reason === "duplicate";

  return (
    <div
      className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4 overflow-y-auto"
      onClick={onClose}
      data-testid="close-po-overlay"
    >
      <div
        className="nb-card bg-white max-w-lg w-full my-8"
        onClick={(e) => e.stopPropagation()}
        data-testid="close-po-dialog"
      >
        <div className="flex items-start justify-between p-5 border-b-2 border-black gap-3">
          <div>
            <div className="label-eyebrow">Cancel this PO · no stock changes</div>
            <h2 className="font-display text-2xl leading-tight mt-1">
              {po.po_number}
            </h2>
            <div className="text-sm text-[#607087] mt-0.5">
              {po.supplier_snapshot?.name || "No supplier"}
            </div>
          </div>
          <button
            data-testid="close-po-x"
            onClick={onClose}
            className="nb-btn bg-white p-2"
            aria-label="close"
          >
            <X size={16} strokeWidth={2.5} />
          </button>
        </div>

        <div className="p-5 flex flex-col gap-3">
          <div className="text-xs bg-[#F0F7FB] border-2 border-[#0A3A7A] text-[#0A3A7A] rounded-lg p-3">
            <strong>Safe:</strong> cancelling a PO only changes its status.
            No movements are created, no stock qty moves, no cost recalcs.
            The PO stays fully visible in Orders history for audit.
          </div>

          <div className="label-eyebrow">Why are you cancelling this PO?</div>
          <div className="flex flex-col gap-2">
            {REASONS.map((r) => {
              const active = reason === r.key;
              const { Icon } = r;
              return (
                <button
                  key={r.key}
                  type="button"
                  data-testid={`close-po-reason-${r.key}`}
                  onClick={() => setReason(r.key)}
                  className={`text-left border-2 rounded-xl p-3 flex gap-3 items-start transition-colors ${
                    active
                      ? "border-black bg-[#FFFBEA]"
                      : "border-black/20 bg-white hover:bg-[#FAFBFC]"
                  }`}
                >
                  <div
                    className="mt-0.5 rounded-md p-1.5 border-2 shrink-0"
                    style={{ borderColor: r.tone, color: r.tone, background: "#fff" }}
                  >
                    <Icon size={16} strokeWidth={2.5} />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-bold text-sm">{r.label}</div>
                    <div className="text-xs text-[#607087] mt-0.5">{r.hint}</div>
                  </div>
                  <div
                    className={`w-4 h-4 rounded-full border-2 shrink-0 mt-1 ${
                      active ? "bg-black border-black" : "border-black/40"
                    }`}
                  />
                </button>
              );
            })}
          </div>

          <div>
            <div className="label-eyebrow mb-1">
              Notes {reason === "other" && <span className="text-[#B0272D]">*required</span>}
            </div>
            <textarea
              data-testid="close-po-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              maxLength={500}
              placeholder={
                reason === "supplier_cancelled"
                  ? "e.g. Rang 14 Feb — factory shutdown, refund credited"
                  : reason === "duplicate"
                    ? "e.g. Duplicate of PO-2026-0007"
                    : reason === "manually_received"
                      ? "Optional — e.g. Booked in on the delivery day before this feature existed"
                      : "Explain why this PO is being closed"
              }
              className="w-full border-2 border-black rounded-lg p-2 text-sm bg-white"
            />
          </div>

          <div className="flex items-center justify-end gap-2 pt-2">
            <button
              type="button"
              data-testid="close-po-cancel"
              onClick={onClose}
              className="nb-btn bg-white px-4 py-2 text-sm"
              disabled={busy}
            >
              Back
            </button>
            <button
              type="button"
              data-testid="close-po-confirm"
              onClick={submit}
              disabled={busy}
              className={`nb-btn px-5 py-2 text-sm font-bold flex items-center gap-2 ${
                willCancel
                  ? "bg-[#FFE4E4] border-[#B0272D] text-[#B0272D]"
                  : "nb-btn-primary"
              }`}
            >
              {chosen?.Icon && <chosen.Icon size={14} strokeWidth={2.5} />}
              {reason === "manually_received"
                ? "Mark as received manually"
                : reason === "supplier_cancelled"
                  ? "Cancel — supplier pulled it"
                  : reason === "duplicate"
                    ? "Cancel — duplicate"
                    : "Confirm cancellation"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ClosePODialog;
